import { useCallback, useState } from "react";
import { Colors } from "../../../../utils/colors";
import { useTheme } from "../../../../hooks/useTheme";
import { NotificationType, ThemeType } from "@markit/common.types";
import { PhoneErrorType } from "@markit/common.utils";
import CustomPhoneTextField from "../../../TextFields/CustomPhoneTextField";
import RectangleButton from "../../../Buttons/RectangleButton";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import { showPhoneInputError } from "../../../../utils/phoneUtils";
import { fetchUserDataFromPhoneNumber } from "../../../../utils/userUtils";
import { userCurrentNumberOfTickets } from "../../../../utils/eventUtils/userTicketUtils";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { useDispatch } from "react-redux";

type UnlockProductByPhoneNumberProps = {
  productId: string;
  hardTheme: ThemeType;
  onUnlocked: () => void;
  alternateSubtext?: string;
};

/**
 * Shown to signed-out users on a CHECKOUT-access product (e.g. non-US
 * numbers, who can never sign in). Renders a small subtext link that opens
 * a modal to enter the phone number they ordered with, looks up whether that
 * number holds an accepted ticket for this product, and unlocks the media for
 * this session if so.
 */
const UnlockProductByPhoneNumber = (props: UnlockProductByPhoneNumberProps) => {
  const { productId, hardTheme, onUnlocked, alternateSubtext } = props;
  const { theme } = useTheme(hardTheme);
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPhoneNumberError, setShowPhoneNumberError] =
    useState<PhoneErrorType>({ isError: false, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const onSubmit = useCallback(async () => {
    setNotFound(false);
    if (showPhoneInputError(phoneNumber, setShowPhoneNumberError)) {
      return;
    }
    setSubmitting(true);
    try {
      const userData = await fetchUserDataFromPhoneNumber(phoneNumber);
      const numTickets = userData
        ? await userCurrentNumberOfTickets(productId, userData.uid)
        : 0;
      if (numTickets > 0) {
        onUnlocked();
        showNotificationBanner(
          dispatch,
          "Access Granted",
          NotificationType.AFFIRMATIVE
        );
      } else {
        setNotFound(true);
      }
    } finally {
      setSubmitting(false);
    }
  }, [phoneNumber, productId, onUnlocked, dispatch]);

  const formContent = (
    <div className="ColumnNormal" style={{ gap: 10 }}>
      <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
        Non-US phone number and already ordered? Enter your number here to
        access the product.
      </span>
      <div style={{ width: "100%" }}>
        <CustomPhoneTextField
          phoneNumber={phoneNumber}
          onPhoneChange={(value: string) => {
            setPhoneNumber(value ?? "");
            setShowPhoneNumberError({ isError: false, message: "" });
            setNotFound(false);
          }}
          showPhoneError={showPhoneNumberError}
          placeholder="Phone number"
          disabled={submitting}
          themeType={hardTheme}
          containerStyles={{ width: "100%" }}
        />
      </div>
      {notFound ? (
        <span style={{ color: Colors.RED3, fontSize: 12 }}>
          We couldn&apos;t find an order associated with this number.
        </span>
      ) : null}
      <RectangleButton
        buttonLabel={submitting ? "Checking..." : "Access"}
        onPress={onSubmit}
        disabled={submitting}
        altTextColor={theme.ButtonText.color}
        altColor={theme.ButtonBG.backgroundColor}
        containerStyles={{ padding: 14 }}
      />
    </div>
  );

  return (
    <>
      <span
        role="button"
        onClick={() => setShowModal(true)}
        style={{
          ...theme.SubText,
          fontSize: 12,
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {alternateSubtext || "Non-US number and already ordered? Click here"}
      </span>
      {showModal ? (
        <PopupModalContainer
          valueComp={formContent}
          theme={theme}
          noExit
          noDivider
          closeModal={() => setShowModal(false)}
          closeOnOutsidePress
          modalContentWidth={PopupModalContainerWidth.SMALL}
        />
      ) : null}
    </>
  );
};

export default UnlockProductByPhoneNumber;
